import { assertUnreachable } from "common/utils/assertUnreachable";
import type {
  WallCoreOperator,
  WallCutOperator,
  WallCladdingOperator,
  WallOffsetOperator,
  WallPlaneOperator,
} from "./wallPlane";

interface WallPlaneOperationBase {
  readonly name: string;
}

export interface WallPlaneCutOperation extends WallPlaneOperationBase {
  readonly operationType: "SET_WALL_CUT";
  readonly wallCutOperator: WallCutOperator;
}

export interface WallPlaneAssemblyOperation extends WallPlaneOperationBase {
  readonly operationType: "SET_WALL_ASSEMBLY";
  readonly wallCladdingNegativeOperator?: WallCladdingOperator<"NEGATIVE_NORMAL">;
  readonly wallCoreOperator?: WallCoreOperator;
  readonly wallCladdingPositiveOperator?: WallCladdingOperator<"POSITIVE_NORMAL">;
  readonly wallOffsetOperator?: WallOffsetOperator;
}

export type WallPlaneOperation = WallPlaneCutOperation | WallPlaneAssemblyOperation;

/** Assembly rule slot used for Studio Lite wall region selection (Wall Framing / Exterior Materials). */
export type PlanarTopologyAssemblySlot = keyof Pick<
  WallPlaneAssemblyOperation,
  "wallCoreOperator" | "wallCladdingPositiveOperator"
>;

/**
 * Yields configured wall-plane operators in fixed apply order: core → offset → finish → cut.
 * Only reads effect fields on the doc operation entity (no presets).
 */
export function* enumerateWallPlaneOperators(operation: WallPlaneOperation): Iterable<WallPlaneOperator> {
  switch (operation.operationType) {
    case "SET_WALL_CUT": {
      yield operation.wallCutOperator;
      break;
    }
    case "SET_WALL_ASSEMBLY": {
      if (operation.wallCoreOperator) {
        yield operation.wallCoreOperator;
      }
      if (operation.wallOffsetOperator) {
        yield operation.wallOffsetOperator;
      }
      if (operation.wallCladdingNegativeOperator) {
        yield operation.wallCladdingNegativeOperator;
      }
      if (operation.wallCladdingPositiveOperator) {
        yield operation.wallCladdingPositiveOperator;
      }
      break;
    }
    default: {
      assertUnreachable(operation);
    }
  }
}
